import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Pencil, Plus, Trash2, X } from 'lucide-react'
import { api, ApiError } from '@/api/client'
import type { ChannelProfile, ChannelProfileInput } from '@/api/client'
import ChannelProfileForm from '@/components/ChannelProfileForm'
import { formatTime } from '@/lib/utils'
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  Table,
} from '@/admin-design/_common'

type ModalState =
  | { mode: 'closed' }
  | { mode: 'create' }
  | { mode: 'edit'; profile: ChannelProfile }

export default function ChannelProfiles() {
  const qc = useQueryClient()
  const [modal, setModal] = useState<ModalState>({ mode: 'closed' })
  const [formError, setFormError] = useState<string | null>(null)
  const [confirmTarget, setConfirmTarget] = useState<ChannelProfile | null>(null)

  const profilesQ = useQuery({
    queryKey: ['channel-profiles'],
    queryFn: api.listProfiles,
  })

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ['channel-profiles'] })

  const createM = useMutation({
    mutationFn: (input: ChannelProfileInput) => api.createProfile(input),
    onSuccess: () => {
      setModal({ mode: 'closed' })
      setFormError(null)
      invalidate()
    },
    onError: (err) => setFormError(errorMessage(err)),
  })

  const updateM = useMutation({
    mutationFn: ({ id, input }: { id: number; input: ChannelProfileInput }) =>
      api.updateProfile(id, input),
    onSuccess: () => {
      setModal({ mode: 'closed' })
      setFormError(null)
      invalidate()
    },
    onError: (err) => setFormError(errorMessage(err)),
  })

  const deleteM = useMutation({
    mutationFn: (id: number) => api.deleteProfile(id),
    onSuccess: () => {
      setConfirmTarget(null)
      invalidate()
    },
  })

  const openCreate = () => {
    setFormError(null)
    setModal({ mode: 'create' })
  }
  const openEdit = (profile: ChannelProfile) => {
    setFormError(null)
    setModal({ mode: 'edit', profile })
  }
  const closeModal = () => {
    setFormError(null)
    setModal({ mode: 'closed' })
  }

  const onSubmit = async (input: ChannelProfileInput) => {
    if (modal.mode === 'create') {
      await createM.mutateAsync(input)
    } else if (modal.mode === 'edit') {
      await updateM.mutateAsync({ id: modal.profile.id, input })
    }
  }

  const busy = createM.isPending || updateM.isPending

  return (
    <div className="space-y-4">
      <header className="flex items-center gap-3">
        <h1 className="text-xl font-semibold text-foreground">Channel Profiles</h1>
        <span className="text-sm text-muted-foreground">
          {profilesQ.data?.length ?? 0}개
        </span>
        <Button
          type="button"
          variant="primary"
          size="sm"
          onClick={openCreate}
          className="ml-auto"
        >
          <Plus size={14} /> New
        </Button>
      </header>

      <Card className="overflow-hidden">
        {profilesQ.isLoading ? (
          <p className="p-6 text-sm text-muted-foreground">불러오는 중...</p>
        ) : profilesQ.isError ? (
          <p className="p-6 text-sm text-destructive">
            로드 실패: {errorMessage(profilesQ.error)}
          </p>
        ) : !profilesQ.data || profilesQ.data.length === 0 ? (
          <EmptyState
            title="아직 등록된 채널 프로필이 없어요."
            description="새 프로필을 추가해보세요."
          />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <Table.Head>
                <Table.Row>
                  <Table.Header>Slug</Table.Header>
                  <Table.Header>Name</Table.Header>
                  <Table.Header>Voice</Table.Header>
                  <Table.Header>Updated</Table.Header>
                  <Table.Header className="w-20 text-right">Actions</Table.Header>
                </Table.Row>
              </Table.Head>
              <Table.Body>
                {profilesQ.data.map((p) => (
                  <Table.Row key={p.id}>
                    <Table.Cell className="font-mono text-xs text-muted-foreground">
                      {p.slug}
                    </Table.Cell>
                    <Table.Cell className="font-medium text-card-foreground">{p.name}</Table.Cell>
                    <Table.Cell>
                      <div className="flex flex-wrap gap-1">
                        {p.voice.slice(0, 5).map((v) => (
                          <Badge key={v} className="text-[11px] rounded">{v}</Badge>
                        ))}
                        {p.voice.length > 5 && (
                          <span className="text-[11px] text-muted-foreground">
                            +{p.voice.length - 5}
                          </span>
                        )}
                      </div>
                    </Table.Cell>
                    <Table.Cell className="text-xs text-muted-foreground">
                      {formatTime(p.updated_at)}
                    </Table.Cell>
                    <Table.Cell>
                      <div className="flex justify-end gap-1">
                        <button
                          onClick={() => openEdit(p)}
                          className="p-1 text-muted-foreground hover:text-info"
                          aria-label="edit"
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          onClick={() => setConfirmTarget(p)}
                          className="p-1 text-muted-foreground hover:text-destructive"
                          aria-label="delete"
                          disabled={deleteM.isPending}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </div>
        )}
      </Card>

      {modal.mode !== 'closed' && (
        <Modal
          title={modal.mode === 'create' ? '새 채널 프로필' : '프로필 편집'}
          onClose={closeModal}
        >
          <ChannelProfileForm
            initial={modal.mode === 'edit' ? modal.profile : undefined}
            onSubmit={onSubmit}
            onCancel={closeModal}
            busy={busy}
            error={formError}
          />
        </Modal>
      )}

      <ConfirmDialog
        open={confirmTarget !== null}
        onOpenChange={(open) => { if (!open) setConfirmTarget(null) }}
        title="채널 프로필 삭제"
        description={
          confirmTarget
            ? `"${confirmTarget.name}" 프로필을 삭제할까요? 이 동작은 되돌릴 수 없어요.`
            : undefined
        }
        confirmLabel="삭제"
        cancelLabel="취소"
        confirmVariant="destructive"
        onConfirm={async () => {
          if (!confirmTarget) return
          await deleteM.mutateAsync(confirmTarget.id)
        }}
      />
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────

interface ModalProps {
  title: string
  onClose: () => void
  children: React.ReactNode
}

// Custom modal kept (not ConfirmDialog) — hosts a long form (ChannelProfileForm)
// rather than a binary confirm/cancel prompt.
function Modal({ title, onClose, children }: ModalProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-auto bg-black/40 p-4">
      <Card className="w-full max-w-xl shadow-xl">
        <Card.Body className="p-5">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-card-foreground">{title}</h2>
            <button
              onClick={onClose}
              className="p-1 text-muted-foreground hover:text-foreground"
              aria-label="close"
            >
              <X size={16} />
            </button>
          </div>
          {children}
        </Card.Body>
      </Card>
    </div>
  )
}

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message
  if (err instanceof Error) return err.message
  return 'unknown error'
}
